import { useSession } from '../context/SessionContext';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  code?: string;
}

export const useApi = () => {
  const { sessionId } = useSession();

  const fetchWithSession = async <T>(
    url: string,
    options: RequestInit = {},
    isStream: boolean = false
  ): Promise<ApiResponse<T> | Response> => {
    try {
      const bodyData = options.body ? JSON.parse(options.body as string) : {};
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: JSON.stringify({
          ...bodyData,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const errorData = await response.json();
            if (errorData.code === 'TOKEN_EXPIRED') {
              return { error: errorData.error, code: errorData.code };
            }
          } catch (e) {
            // JSON parsing failed, fall through to default error handling
          }
        }
        
        // Handle 403 errors (trial expired, license expired, no license)
        if (response.status === 403) {
          try {
            const errorData = await response.json();
            // Return the detailed error info from the backend
            return { 
              error: errorData.message || errorData.error || 'Access denied',
              code: errorData.error // error code like 'trial_expired', 'license_expired', 'no_license'
            };
          } catch (e) {
            // JSON parsing failed, fall through to default error handling
          }
        }
        
        return { error: response.statusText || 'API request failed' };
      }

      if (isStream) {
        return response;
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return { fetchWithSession };
};